import {
  Button,
  Card,
  Container,
  Group,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { Trans, useTranslation } from "react-i18next";
import { IconMail } from "@tabler/icons-react";
import { PricingBoxes } from "./PricingBoxes";
import { usePlan } from "../hooks/usePlan";

function CreditsTable() {
  const { t } = useTranslation("common");

  // Keep these in step with FacetFluxPlanLimitationsService: the import row and enrichment rates are per
  // block of 100 attributes (BaseCreditsPerImportRow = 50, BaseCreditsPerProduct = 100, ×1.5 Enhanced,
  // ×2 Deep), schema inference is 50 and a repeat diagnostic is 250. The old table under-quoted enrichment
  // by 3-6x and claimed schema analysis was free, which it is not.
  const rows = [
    { action: t("pricing.credits.rowImport"), credits: "50" },
    { action: t("pricing.credits.rowEnrichment"), credits: "100 – 200" },
    { action: t("pricing.credits.rowTranslation"), credits: "10 – 20" },
    { action: t("pricing.credits.rowSchemaInference"), credits: "50" },
    { action: t("pricing.credits.rowDiagnostic"), credits: "250" },
  ];

  return (
    <Card withBorder>
      <Text fw={600} mb="sm">{t("pricing.credits.title")}</Text>
      <Text size="sm" c="dimmed" mb="md">
        {t("pricing.credits.description")}
      </Text>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("pricing.credits.operationHeader")}</Table.Th>
            <Table.Th ta="right">{t("pricing.credits.creditsHeader")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((r) => (
            <Table.Tr key={r.action}>
              <Table.Td><Text size="sm">{r.action}</Text></Table.Td>
              <Table.Td ta="right"><Text size="sm" fw={500}>{r.credits}</Text></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}

export default function PricingPage() {
  const { t } = useTranslation("common");
  const { plan } = usePlan();

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <div style={{ textAlign: "center" }}>
          <Title order={2}>{t("pricing.page.title")}</Title>
          <Text c="dimmed" size="sm" mt="xs">
            {t("pricing.page.subtitle")}
          </Text>
          {plan && plan !== "Free" && (
            <Text size="sm" mt="xs">
              <Trans
                i18nKey="pricing.page.currentPlan"
                values={{ plan }}
                components={[<strong key="0" />]}
              />
            </Text>
          )}
        </div>

        <PricingBoxes />

        <CreditsTable />

        <Card withBorder>
          <Group justify="space-between" wrap="wrap">
            <div>
              <Text fw={600}>{t("pricing.customSolution.title")}</Text>
              <Text size="sm" c="dimmed">
                {t("pricing.customSolution.description")}
              </Text>
            </div>
            <Button
              component="a"
              href="mailto:sales@autarky.dev"
              variant="outline"
              leftSection={<IconMail size={16} />}
            >
              {t("pricing.contactSales")}
            </Button>
          </Group>
        </Card>

        <Text ta="center" size="xs" c="dimmed">
          {t("pricing.page.footer")}
        </Text>
      </Stack>
    </Container>
  );
}
